import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { createContext, type FC, type ReactNode, useContext } from "react";
import {
  InteractiveReferenceBoundary,
  useIsInsideInteractiveReference,
} from "./interactiveReferenceBoundary.js";

export interface TeammateReference {
  teammateId: TeammateId;
  /**
   * The playbook whose teammate page the reference links into, when the
   * content or its surface knows one. The host picks a fallback otherwise.
   */
  playbookId?: PlaybookId;
}

export interface TeammateReferenceWrapperProps {
  reference: TeammateReference;
  /** Base styling shared by the linked and inert reference. */
  className: string;
  children: ReactNode;
}

/**
 * An app-supplied component that turns a teammate reference into a link to
 * the teammate's page. Apps that don't supply one render references as
 * styled text.
 */
export type TeammateReferenceWrapper = FC<TeammateReferenceWrapperProps>;

const TeammateReferenceContext = createContext<
  TeammateReferenceWrapper | undefined
>(undefined);

export const TeammateReferenceProvider = TeammateReferenceContext.Provider;

/**
 * Renders a teammate reference from the AST: through the host wrapper from
 * `TeammateReferenceProvider` when one is supplied, otherwise as a styled
 * span. Inside another interactive reference it renders as plain content.
 * Exists as a component so the plain-function AST renderer can consume the
 * context.
 */
export const TeammateReferenceNode: FC<TeammateReferenceWrapperProps> = (
  props,
) => {
  const { reference, className, children } = props;
  const Wrapper = useContext(TeammateReferenceContext);
  const isInsideInteractiveReference = useIsInsideInteractiveReference();
  if (isInsideInteractiveReference) {
    return <>{children}</>;
  }
  if (!Wrapper) {
    return <span className={className}>{children}</span>;
  }
  return (
    <Wrapper reference={reference} className={className}>
      <InteractiveReferenceBoundary>{children}</InteractiveReferenceBoundary>
    </Wrapper>
  );
};
